import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import './Auth.css';
import { Theme } from "../../theme/Theme";
import { commonStyles } from "../../utils/styles/CommonStyle";
import { Icon } from "@iconify/react";
import CommonButton from "../../components/common/CommonButton";
import colors from "../../theme/colors";
import { useDispatch, useSelector } from "react-redux";
import { SignUpActioninitiate } from "../../redux/actions/auth/signUpAction";
import { Loader } from "../../utils";
import { useFormik } from "formik";
import * as Yup from "yup";
import { nameValidation, phoneValidation, strongPasswordValidation, confirmPasswordValidation } from "../../utils/validations";
import AuthLayout from "../../components/common/AuthLayout";
import LegalContent from "../../components/common/LegalContent";

export default function Signup() {
  const location = useLocation();
  const navigate = useNavigate();

  const isBusinessPath = location.pathname.includes("business");
  const [userType, setUserType] = useState(isBusinessPath ? "business" : "user");
  const [tabSide, setTabSide] = useState(isBusinessPath ? 1 : 0);

  useEffect(() => {
    const b = location.pathname.includes("business");
    setUserType(b ? "business" : "user");
    setTabSide(b ? 1 : 0);
  }, [location.pathname]);

  const [showPW, setShowPW] = useState(false);
  const [showCPW, setShowCPW] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);  // Prevent duplicate submissions

  const dispatch = useDispatch();
  const { loading: signupLoading } = useSelector((state) => state.signUp);
  const { loading: sendOtpLoading } = useSelector((state) => state.sendOtp);

  // Track overall loading state through both signup and sendOtp processes
  const isLoading = signupLoading || sendOtpLoading || isSubmitting;

  const formik = useFormik({
    initialValues: {
      name: "",
      phone_number: "",
      password: "",
      password_confirmation: "",
      agreeTerms: false,
    },
    validationSchema: Yup.object({
      name: nameValidation,
      phone_number: phoneValidation,
      password: strongPasswordValidation,
      password_confirmation: confirmPasswordValidation('password'),
      agreeTerms: Yup.boolean().oneOf([true], "You must agree to the terms and conditions"),
    }),
    onSubmit: async (values) => {
      // Prevent duplicate submissions
      if (isSubmitting) return;

      setIsSubmitting(true);
      try {
        const cleanPhone = values.phone_number.replace(/^\+91\s?/, '');
        const payload = { ...values, phone_number: cleanPhone, account_type: userType };
        const result = await dispatch(SignUpActioninitiate(payload));
        if (result?.success) {
          // Wait for navigation to ensure smooth transition
          // SendOTP loading will automatically stop when complete
          navigate("/verify-otp");
        }
      } catch (error) {
        console.error("Signup error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout
      title="Create an Account"
      subtitle={userType === "business" ? "Create your business account" : "Join to explore local businesses near you."}
      showTabs={true}
      tabSide={tabSide}
      switchTabRoutePrefix="/signup"
      showLegalLinks={false}
    >
      <form autoComplete="off" onSubmit={formik.handleSubmit} className="w-100 mt-4">
        <input
          type="text"
          name="name"
          placeholder={userType === "business" ? "Business Name" : "Name"}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          style={{
            ...commonStyles.input,
            ...Theme.inputText,
            ...(formik.touched.name && formik.errors.name ? commonStyles.errorBorder : commonStyles.commonBorder)
          }}
        />
        {formik.touched.name && formik.errors.name && (
          <div style={Theme.errorText}>
            {userType === "business" ? "Business name is required" : formik.errors.name}
          </div>
        )}

        <input
          type="tel"
          name="phone_number"
          placeholder="Phone number"
          className="mt-3"
          value={formik.values.phone_number}
          maxLength={10}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
            formik.setFieldValue('phone_number', val);
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 10);
            formik.setFieldValue('phone_number', pasted);
          }}
          onBlur={formik.handleBlur}
          style={{
            ...commonStyles.input,
            ...Theme.inputText,
            ...(formik.touched.phone_number && formik.errors.phone_number ? commonStyles.errorBorder : commonStyles.commonBorder)
          }}
        />
        {formik.touched.phone_number && formik.errors.phone_number && <div style={Theme.errorText}>{formik.errors.phone_number}</div>}

        <div style={{ position: 'relative', marginTop: 8, display: 'flex', alignItems: 'center' }} className="mt-3">
          <input
            type={showPW ? "text" : "password"}
            name="password"
            placeholder="Password"
            autoComplete="current-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{
              ...commonStyles.input,
              ...Theme.inputText,
              ...(formik.touched.password && formik.errors.password ? commonStyles.errorBorder : commonStyles.commonBorder)
            }}
          />
          <span
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', userSelect: 'none', marginTop: 4
            }}
            onClick={() => setShowPW(v => !v)} tabIndex={0} role="button"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowPW(v => !v); } }}
          >
            <Icon icon={showPW ? "qlementine-icons:eye-crossed-16" : "mdi-light:eye"} width="20" height="20" style={{ marginBottom: 6, color: "var(--text-secondary)" }} />
          </span>
        </div>
        {formik.touched.password && formik.errors.password && <div style={Theme.errorText}>{formik.errors.password}</div>}

        <div style={{ position: 'relative', marginTop: 8, display: 'flex', alignItems: 'center' }} className="mt-3">
          <input
            type={showCPW ? "text" : "password"}
            name="password_confirmation"
            placeholder="Confirm Password"
            autoComplete="current-password"
            value={formik.values.password_confirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{
              ...commonStyles.input,
              ...Theme.inputText,
              ...(formik.touched.password_confirmation && formik.errors.password_confirmation ? commonStyles.errorBorder : commonStyles.commonBorder)
            }}
          />
          <span
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', userSelect: 'none', marginTop: 4
            }}
            onClick={() => setShowCPW(v => !v)} tabIndex={0} role="button"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowCPW(v => !v); } }}
          >
            <Icon icon={showCPW ? "qlementine-icons:eye-crossed-16" : "mdi-light:eye"} width="20" height="20" style={{ marginBottom: 6, color: "var(--text-secondary)" }} />
          </span>
        </div>
        {formik.touched.password_confirmation && formik.errors.password_confirmation && <div style={Theme.errorText}>{formik.errors.password_confirmation}</div>}

        <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            id="agreeTerms"
            name="agreeTerms"
            checked={formik.values.agreeTerms}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ marginRight: 8, marginTop: 2, width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-success)', flexShrink: 0 }}
          />
          <label htmlFor="agreeTerms" style={{ ...Theme.font14Regular, fontWeight: 400, userSelect: 'none', cursor: 'pointer', lineHeight: 1.5 }}>
            I agree to the <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTermsOpen(true); }} style={{ color: colors.mainColor, fontWeight: 600, textDecoration: 'underline' }}>terms & conditions</span> and <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPrivacyOpen(true); }} style={{ color: colors.mainColor, fontWeight: 600, textDecoration: 'underline' }}>privacy policy</span>
          </label>
        </div>
        {formik.touched.agreeTerms && formik.errors.agreeTerms && <div style={{ ...Theme.errorText, marginTop: 4 }}>{formik.errors.agreeTerms}</div>}

        <div className="d-flex justify-content-center mx-auto mb-2 w-100 mt-4">
          <CommonButton
            title={isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Loader variant="dots" size="small" color="white" />
                <span>
                  {signupLoading ? "Creating Account..." :
                   sendOtpLoading ? "Sending OTP..." :
                   "Processing..."}
                </span>
              </div>
            ) : "Create Account"}
            type="submit"
            width={"100%"}
            disabled={isLoading || !formik.isValid}
          />
        </div>
      </form>
      <div style={{ marginTop: 28 }} className="w-100">
        <span style={Theme.modalDescription} className="d-flex justify-content-center">Already have an account?</span>
        <Link to={userType === "business" ? "/business/login" : "/login"} style={{ display: 'inline-block', width: '100%' }} className="text-decoration-none">
          <CommonButton title="Log In Now" onClick={() => { }} width="100%" variant="outline" />
        </Link>
      </div>

      <LegalContent
        type="terms"
        isModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
      />
      <LegalContent
        type="privacy"
        isModal
        isOpen={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
      />
    </AuthLayout>
  );
}
